import type { ComponentProps } from 'react';

import { validateApplication } from '#/api/ats';
import { FilePickerField } from '#/components/Form/FilePickerField';
import { useI18n } from '#/i18n/hooks/useI18n';
import { RequestedAttachmentUserField } from '#/modules/listing/components/ApplicationFields/RequestedAttachmentUserField';

import { ListingViewAtsApplicationForm } from './ListingViewAtsApplicationForm';

/**
 * Wrapper for ListingViewAtsApplicationForm to be used in the
 * idealist.org website, not on the widget
 */
export function ListingViewIdealistAtsUserApplicationForm({
  requestedAttachments,
  listingType,
  listingId,
  submission,
}: Omit<
  ComponentProps<typeof ListingViewAtsApplicationForm>,
  | 'renderRequestedAttachmentFileField'
  | 'renderResumeField'
  | 'submitButtonText'
>) {
  const { i18n } = useI18n();

  return (
    <ListingViewAtsApplicationForm
      requestedAttachments={requestedAttachments}
      listingId={listingId}
      listingType={listingType}
      submission={submission}
      submitButtonText={
        listingType === 'VOLOP'
          ? i18n('Submit Your Form')
          : i18n('Submit Application')
      }
      renderRequestedAttachmentFileField={({
        key,
        name,
        label,
        disabled,
        requestedFileAttachment,
      }) => (
        <RequestedAttachmentUserField
          key={key}
          name={name}
          label={label}
          disabled={disabled}
          analyticsLabel={requestedFileAttachment.label}
          mimetypes={requestedFileAttachment.mimetypes}
          downloadAttachment={false}
          fileType="LISTING_APPLICATION"
          listingType={listingType}
        />
      )}
      renderResumeField={({
        name,
        label,
        disabled,
        description,
        accept,
        isNewResume,
        extraAnalyticsData,
        onChange,
        onError,
      }) => (
        <FilePickerField
          name={name}
          disabled={disabled}
          label={label}
          description={description}
          downloadAttachment={false}
          fileType="USER_RESUME"
          accept={accept}
          showDate={!isNewResume}
          onChange={(fileId) => {
            validateApplication({ resumeS3FileId: fileId }, { partial: true })
              .then(() => {
                onChange(fileId);
                return;
              })
              .catch((error) => {
                onError(error as Error, fileId);
              });
          }}
          extraAnalyticsData={extraAnalyticsData}
        />
      )}
    />
  );
}
